import * as React from 'react';
import { Form, FormGroup, Label, Input, Button, FormText, FormFeedback, Spinner } from 'reactstrap';
import { getValueFromInputRef, getNameFromInputRef, getJson, postJson } from '../../../../../../Services';
import { IValidationResult, DefaultValidationResult, IResources } from '../../../../../../Types';

import '../Styles/OrganisationRegistrationForm.css';
import Endpoints from '../../../../../../Utils/Endpoints';

interface IOrganisationValidation {
    [key: string]: IValidationResult;
}

enum OrganisationRegistrationFormStatus {
    Initial,
    Loading,
    Error
}

// Yes, it has to be called grecaptcha like this or it won't work. 
// See https://developers.google.com/recaptcha/docs/display for more information
//var grecaptcha: any;
//var verilite_registration_captcha_callback: () => void;
//var verilite_registration_captcha_expired_callback: () => void;

interface IOrganisationRegistrationFormProps {
    resources: IResources;
    onRegisterComplete: () => void;
}

interface IOrganisationRegistrationFormState {
    validation: IOrganisationValidation;
    status: OrganisationRegistrationFormStatus;
}

export class OrganisationRegistrationForm extends React.Component<IOrganisationRegistrationFormProps, IOrganisationRegistrationFormState>{

    orgNameInputRef: React.RefObject<HTMLInputElement>;
    emailInputRef: React.RefObject<HTMLInputElement>;
    displayNameInputRef: React.RefObject<HTMLInputElement>;
    passwordInputRef: React.RefObject<HTMLInputElement>;
    confirmPasswordInputRef: React.RefObject<HTMLInputElement>;
    passwordRegex: RegExp;
    captchaSiteKey: string;
    captchaValidationKey: string = "captcha";
    appError: string;

    constructor(props: any) {
        super(props);

        this.state = {
            validation: {
                organisationName: DefaultValidationResult,
                email: DefaultValidationResult,
                displayName: DefaultValidationResult,
                password: DefaultValidationResult,
                confirmPassword: DefaultValidationResult,
                captcha: DefaultValidationResult
            },
            status: OrganisationRegistrationFormStatus.Initial
        };

        this.orgNameInputRef = React.createRef<HTMLInputElement>();
        this.emailInputRef = React.createRef<HTMLInputElement>();
        this.displayNameInputRef = React.createRef<HTMLInputElement>();
        this.passwordInputRef = React.createRef<HTMLInputElement>();
        this.confirmPasswordInputRef = React.createRef<HTMLInputElement>();
        this.passwordRegex = /^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])|(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[^a-zA-Z0-9])|(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[^a-zA-Z0-9])|(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^a-zA-Z0-9])).{8,16}$/;
        this.captchaSiteKey = process.env.REACT_APP_CAPTCHASITEKEY;
        this.appError = "";

        // Initialize reCAPTCHA handlers
        window.verilite_registration_captcha_callback = () => {
            this.setState({ validation: { ...this.state.validation, [this.captchaValidationKey]: { isValid: true, reason: "" } } });
        };

        window.verilite_registration_captcha_expired_callback = () => {
            this.setState({ validation: { ...this.state.validation, [this.captchaValidationKey]: { isValid: false, reason: this.props.resources.Registration_Validation_RecaptchaExpired } } });
        };
    }
    
    validate = (ref: React.RefObject<HTMLInputElement>, regex: RegExp, invalidErrorMessage: string): boolean => {
        var validation = this.state.validation;
        var value = getValueFromInputRef(ref);
        var name = getNameFromInputRef(ref);

        if (!!value) {
            if (!regex.test(value)) {
                validation[name] = { isValid: false, reason: invalidErrorMessage };
            } else {
                validation[name] = { isValid: true, reason: "" };
            }
        } else {
            validation[name] = { isValid: false, reason: this.props.resources.Registration_Validation_RequiredField };
        }

        this.setState({ validation: validation });

        return validation[name].isValid;
    };

    validateOrganisation = () => {
        this.validate(this.orgNameInputRef, /^([A-Za-z0-9-]\s?){1,}$/, this.props.resources.Registration_Validation_InvalidOrganisationId);
    };

    validateEmail = () => {
        this.validate(this.emailInputRef, /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/, this.props.resources.Registration_Validation_EmailIsNotValid);
    };

    validateDisplayName = () => {
        this.validate(this.displayNameInputRef, /^\S+.{1,}\S$/, this.props.resources.InvalidDisplayName);
    };

    passwordValidation = (thisPasswordRef: React.RefObject<HTMLInputElement>, otherPasswordRef: React.RefObject<HTMLInputElement>) => {
        var thisPassword = getValueFromInputRef(thisPasswordRef);
        var otherPassword = getValueFromInputRef(otherPasswordRef);

        if (!!thisPassword && !!otherPassword) {
            if (thisPassword == otherPassword) {
                if (this.validate(thisPasswordRef, this.passwordRegex, this.props.resources.Registration_Validation_Password.replace("\\\\", "\\").replace("\\\"", "\""))) {
                    this.validate(otherPasswordRef, this.passwordRegex, this.props.resources.Registration_Validation_Password.replace("\\\\", "\\").replace("\\\"", "\""));
                }
                else {
                    //if passwords match but are invalid clear validation on the other password field so as not to clutter the ui with duplicate/inaccurate errors
                    this.setState(
                        {
                            validation: {
                                ...this.state.validation,
                                [getNameFromInputRef(otherPasswordRef)]: { isValid: true, reason: "" }
                            }
                        }
                    );
                }
            } else {
                this.setState(
                    {
                        validation: {
                            ...this.state.validation,
                            [getNameFromInputRef(thisPasswordRef)]: {
                                isValid: false,
                                reason: this.props.resources.Registration_Validation_PasswordsDoNotMatch
                            },
                            [getNameFromInputRef(otherPasswordRef)]: { isValid: true, reason: "" }
                        }
                    }
                );
            }
        } else {
            this.validate(thisPasswordRef, this.passwordRegex, this.props.resources.Registration_Validation_Password.replace("\\\\", "\\").replace("\\\"", "\""));
        }
    };

    validatePassword = () => {
        this.passwordValidation(this.passwordInputRef, this.confirmPasswordInputRef);
    };

    validateConfirmPassword = () => {
        this.passwordValidation(this.confirmPasswordInputRef, this.passwordInputRef);
    };

    checkValidation = (): boolean => {
        var validationResult = true;
        for (let key in this.state.validation) {
            validationResult = this.state.validation[key].isValid && validationResult;
        }
        return validationResult;
    };

    allFieldsHaveValues = () => {
        var result = true;

        result = result && getValueFromInputRef(this.orgNameInputRef) != "";
        result = result && getValueFromInputRef(this.emailInputRef) != "";
        result = result && getValueFromInputRef(this.displayNameInputRef) != "";
        result = result && getValueFromInputRef(this.passwordInputRef) != "";
        result = result && getValueFromInputRef(this.confirmPasswordInputRef) != "";
        result = result && this.tryGetRecaptchaResponse().success;

        if (this.state.status == OrganisationRegistrationFormStatus.Error && result) {
            this.setState({ status: OrganisationRegistrationFormStatus.Initial });
        }

        return result;
    };

    tryGetRecaptchaResponse = (): { success: boolean; result: any; } => {
        var success = false;
        var result = null;
        try {
            result = window.grecaptcha.getResponse();
            success = result != "";
        } catch (e) {
            success = false;
        }
        return { success: success, result: result };
    };

    handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!this.allFieldsHaveValues()) {
            return;
        }
        var recaptcha = this.tryGetRecaptchaResponse();
        window.grecaptcha.reset();
        this.setState({ status: OrganisationRegistrationFormStatus.Loading });

        var body = JSON.stringify({
            OrganisationId: getValueFromInputRef(this.orgNameInputRef),
            AdminEmail: getValueFromInputRef(this.emailInputRef),
            AdminDisplayName: getValueFromInputRef(this.displayNameInputRef),
            UserRole: "",
            Password: getValueFromInputRef(this.passwordInputRef),
            ConfirmPassword: getValueFromInputRef(this.confirmPasswordInputRef),
            ReCaptcha: recaptcha.result
        });
        postJson(Endpoints.RegisterOrganisation, body, undefined, false, undefined, true, undefined, true).then((r) => {
            if (r) {
                this.props.onRegisterComplete();
            }
            else {
                this.appError = this.props.resources.SomethingWentWrong;
                this.setState({ status: OrganisationRegistrationFormStatus.Error });
            }
        }).catch((e) => {
            if (e.AppError != undefined) {
                this.appError = e.AppError[0];
            } else {
                console.error(e);
                this.appError = this.props.resources.SomethingWentWrong;
            }
            this.setState({ status: OrganisationRegistrationFormStatus.Error });
        });
    };

    render() {
        return (
            <Form onSubmit={this.handleSubmit}>
                <FormGroup>
                    <Label for="organisationName">{this.props.resources?.Registration_OrganisationId}</Label>
                    <Input type="text" name="organisationName" id="organisationName" innerRef={this.orgNameInputRef} onBlur={this.validateOrganisation} invalid={!this.state.validation.organisationName.isValid} />
                    <FormFeedback tooltip className="invalid-tooltip-relative">{this.state.validation.organisationName.reason}</FormFeedback>
                </FormGroup>
                <FormGroup>
                    <Label for="email">{this.props.resources?.Registration_Email}</Label>
                    <Input type="email" name="email" id="email" innerRef={this.emailInputRef} onBlur={this.validateEmail} invalid={!this.state.validation.email.isValid} />
                    <FormFeedback tooltip className="invalid-tooltip-relative">{this.state.validation.email.reason}</FormFeedback>
                </FormGroup>
                <FormGroup>
                    <Label for="displayName">{this.props.resources?.Registration_DisplayName}</Label>
                    <Input type="text" name="displayName" id="displayName" innerRef={this.displayNameInputRef} onBlur={this.validateDisplayName} invalid={!this.state.validation.displayName.isValid} />
                    <FormFeedback tooltip className="invalid-tooltip-relative">{this.state.validation.displayName.reason}</FormFeedback>
                </FormGroup>
                <FormGroup>
                    <Label for="password">{this.props.resources?.Registration_Password}</Label>
                    <Input type="password" name="password" id="password" innerRef={this.passwordInputRef} onBlur={this.validatePassword} invalid={!this.state.validation.password.isValid} />
                    <FormFeedback tooltip className="invalid-tooltip-relative">{this.state.validation.password.reason}</FormFeedback>
                </FormGroup>
                <FormGroup>
                    <Label for="confirmPassword">{this.props.resources?.Registration_ConfirmPassword}</Label>
                    <Input type="password" name="confirmPassword" id="confirmPassword" innerRef={this.confirmPasswordInputRef} onBlur={this.validateConfirmPassword} invalid={!this.state.validation.confirmPassword.isValid} />
                    <FormFeedback tooltip className="invalid-tooltip-relative">{this.state.validation.confirmPassword.reason}</FormFeedback>
                </FormGroup>

                {this.state.status == OrganisationRegistrationFormStatus.Loading &&
                    <div className="organisation-registration-form-spinner-container mt-4">
                        <Spinner style={{ width: '3rem', height: '3rem' }} color="light" />
                        <span className="my-2">{this.props.resources.Registration_WorkingOnIt}</span>
                    </div>
                }
                <div>
                    <FormGroup style={this.state.status == OrganisationRegistrationFormStatus.Loading ? { display: "none" } : { display: "block" }}>
                        <div className="g-recaptcha" data-sitekey={this.captchaSiteKey}
                            data-callback="verilite_registration_captcha_callback"
                            data-expired-callback="verilite_registration_captcha_expired_callback">
                        </div>
                        {this.state.status == OrganisationRegistrationFormStatus.Error && <FormFeedback tooltip className="invalid-tooltip-relative" style={{ display: "block" }}>{this.appError}</FormFeedback>}
                    </FormGroup>

                    {this.state.status != OrganisationRegistrationFormStatus.Loading &&
                        <Button outline color="light" className={`float-right ${this.allFieldsHaveValues() && this.checkValidation() ? "" : "disabled"}`}>{this.props.resources.Submit}</Button>
                    }
                </div>
            </Form>
        );
    }
}